import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Box,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import { commonInlineStyles } from "../../styles";
import MuiAlert from "@material-ui/lab/Alert";
import { useLocation } from "react-router-dom";
import { appUrl } from "../../../../../../../utils/config";
import axios from "axios";
import { useApiBaseUrl } from "../../../../common/CommonMethods/CommonMethods";

const Alert = (props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};


function denormalizeAllocationConfig(backendData, poStatuses) {
  // Helper function to get default PO status name
  const getDefaultStatusName = (preferredStatus) => {
    if (poStatuses && poStatuses.length > 0) {
      const preferred = poStatuses.find(status => status.status_name.toLowerCase() === (typeof preferredStatus === 'string' ? preferredStatus.toLowerCase() : preferredStatus));
      return preferred ? preferred.status_name : poStatuses[0].status_name;
    }
    return preferredStatus;
  };

  // Helper function to convert status ID to status name
  const getStatusNameById = (statusId) => {
    if (poStatuses && poStatuses.length > 0 && statusId) {
      const status = poStatuses.find(status => status.status_id === statusId);
      return status ? status.status_name : statusId.toString();
    }
    return statusId ? statusId.toString() : "";
  };

  // Helper function to normalize CMO-related status names
  const normalizeCMOStatus = (statusValue) => {
    if (!statusValue || typeof statusValue !== 'string') return statusValue;
    
    const lowerValue = statusValue.toLowerCase();
    
    // Check for various CMO-related status patterns
    if (lowerValue.includes('shared with cmo') || 
        lowerValue.includes('shared with cmo instantly') || 
        lowerValue.includes('new po') && lowerValue.includes('shared')) {
      
      // Find the exact status name from poStatuses that contains "shared" and "cmo"
      const sharedStatus = poStatuses?.find(status => {
        const statusName = status.status_name.toLowerCase();
        return statusName.includes('shared') && statusName.includes('cmo');
      });
      
      return sharedStatus ? sharedStatus.status_name : statusValue;
    }
    
    return statusValue;
  };

  // Helper function to handle both status ID and status name values
  const getStatusValue = (value, triggerType) => {
    if (!value) return "";
    
    // If triggerType is not "poStatus", return the value as-is
    if (triggerType !== "poStatus") {
      return value.toString();
    }
    
    // If value is a number, treat it as status ID
    if (typeof value === 'number' || !isNaN(value)) {
      return normalizeCMOStatus(getStatusNameById(value));
    }
    
    // If value is a string, it might be a status name already
    return normalizeCMOStatus(value);
  };

  return {
    showExpiredLots: backendData.showExpiredLots === true ? "YES" : "NO",
    defaultAllocationReq: backendData.defaultAllocationReq === true ? "YES" : "NO",
    qtyVarianceAcceptanceLimit: backendData.qtyVarianceAcceptanceLimit?.toString() || "10",
    allowMultipleLotAllocation: backendData.allowMultipleLotAllocation === true ? "YES" : "NO",
    allowOverAllocation: backendData.allowOverAllocation === undefined || backendData.allowOverAllocation === null ? "NA" : backendData.allowOverAllocation === true ? "YES" : "NO",
    overAllocationLimit: backendData.overAllocationLimit === undefined || backendData.overAllocationLimit === null ? "NA" : backendData.overAllocationLimit?.toString() || "50",
    holdTimeBtnCreatedAndSWCMO: (backendData.holdTimeBtnCreatedAndSWCMO === undefined || backendData.holdTimeBtnCreatedAndSWCMO === null || backendData.holdTimeBtnCreatedAndSWCMO === 0) ? "0" : backendData.holdTimeBtnCreatedAndSWCMO?.toString() || "12",
    allowLotAllocationBy: backendData.allowLotAllocationBy === "all" ? "Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)" : backendData.allowLotAllocationBy || "Sponsor",
    sponsorTriggerStart: backendData.sponsorTriggerStart === undefined || backendData.sponsorTriggerStart === null ? "PO Status" : 
      backendData.sponsorTriggerStart === "poStatus" ? "PO Status" :
      backendData.sponsorTriggerStart === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
      "No. of days before expected delivery date",
    sponsorTriggerEnd: backendData.sponsorTriggerEnd === undefined || backendData.sponsorTriggerEnd === null ? "No. of days before expected delivery date" : 
      backendData.sponsorTriggerEnd === "poStatus" ? "PO Status" :
      backendData.sponsorTriggerEnd === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
      "No. of days before expected delivery date",
    cmoTriggerStart: backendData.cmoTriggerStart === undefined || backendData.cmoTriggerStart === null ? "PO Status" : 
      backendData.cmoTriggerStart === "poStatus" ? "PO Status" :
      backendData.cmoTriggerStart === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
      "No. of days before expected delivery date",
    cmoTriggerEnd: backendData.cmoTriggerEnd === undefined || backendData.cmoTriggerEnd === null ? "No. of days before expected delivery date" : 
      backendData.cmoTriggerEnd === "poStatus" ? "PO Status" :
      backendData.cmoTriggerEnd === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
      "No. of days before expected delivery date",
    sponsorTriggerStartValue: backendData.sponsorTriggerStartValue === undefined || backendData.sponsorTriggerStartValue === null ? 
      getDefaultStatusName("Created") : 
      getStatusValue(backendData.sponsorTriggerStartValue, backendData.sponsorTriggerStart),
    sponsorTriggerEndValue: backendData.sponsorTriggerEndValue === undefined || backendData.sponsorTriggerEndValue === null ? 
      "10" : 
      getStatusValue(backendData.sponsorTriggerEndValue, backendData.sponsorTriggerEnd),
    cmoTriggerStartValue: backendData.cmoTriggerStartValue === undefined || backendData.cmoTriggerStartValue === null ? 
      getDefaultStatusName("Confirmed") : 
      getStatusValue(backendData.cmoTriggerStartValue, backendData.cmoTriggerStart),
    cmoTriggerEndValue: backendData.cmoTriggerEndValue === undefined || backendData.cmoTriggerEndValue === null ? 
      "10" : 
      getStatusValue(backendData.cmoTriggerEndValue, backendData.cmoTriggerEnd),
    // Under allocation fields (mirroring over allocation style)
    allowUnderAllocation: backendData.allowUnderAllocation === undefined || backendData.allowUnderAllocation === null
      ? "NO"
      : backendData.allowUnderAllocation === true
        ? "YES"
        : "NO",
    underAllocationLimit: backendData.allowUnderAllocationLimit === undefined || backendData.allowUnderAllocationLimit === null
      ? "10"
      : backendData.allowUnderAllocationLimit?.toString() || "10",
  };
}

const AllocationConfigurations = ({
  formData,
  updateStepData,
  classes,
  handleNext,
  handleBack,
  handleSubmit,
  activeStep,
  stepsLength,
  readOnly = false,
  isAllocationConfigEditMode = false, 
  cameFromBack = false,
  onValidationChange = () => { },
  markApiCallSuccessful,
}) => {

  const location = useLocation()
  const selectedCollabrixID = location.state?.productDetails?.collabrixProductID;
  const [updateModeData, setUpdateModeData] = useState(null);
  const selectedProductFormData = location.state?.selectedProductFormData;
  const addNewProductConfig = location.state?.addNewProductConfig;
  
  const viewOnlyProductDetails = location.state?.viewProductDetails;
  const editPendingStatus = location.state?.isPending && location.state?.isEdit;

  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
    key: Date.now(),
  });
  const [apiPoStatuses, setApiPoStatuses] = useState([]);
  const [stepData, setStepData] = useState(() => {
    const baseFormData = formData?.allocationConfiguration || {};
    const baseSelectedProductFormData = selectedProductFormData?.allocationConfiguration || {};
    const initialSponsorOnly = (baseFormData?.allowLotAllocationBy ?? baseSelectedProductFormData?.allowLotAllocationBy ?? "Sponsor") === "Sponsor";
    return {
  showExpiredLots: baseFormData?.showExpiredLots ?? baseSelectedProductFormData?.showExpiredLots ?? "NO",
  defaultAllocationReq: baseFormData?.defaultAllocationReq ?? baseSelectedProductFormData?.defaultAllocationReq ?? "NO",
  qtyVarianceAcceptanceLimit: baseFormData?.qtyVarianceAcceptanceLimit ?? baseSelectedProductFormData?.qtyVarianceAcceptanceLimit ?? "10",
  allowLotAllocationBy: baseFormData?.allowLotAllocationBy ?? baseSelectedProductFormData?.allowLotAllocationBy ?? "Sponsor",
  holdTimeBtnCreatedAndSWCMO: initialSponsorOnly ? "0" : (baseFormData?.holdTimeBtnCreatedAndSWCMO ?? baseSelectedProductFormData?.holdTimeBtnCreatedAndSWCMO ?? "12"),
  sponsorTriggerStart: baseFormData?.sponsorTriggerStart ?? baseSelectedProductFormData?.sponsorTriggerStart ?? "PO Status",
  sponsorTriggerStartValue: baseFormData?.sponsorTriggerStartValue ?? baseSelectedProductFormData?.sponsorTriggerStartValue ?? "Created",
  sponsorTriggerEnd: baseFormData?.sponsorTriggerEnd ?? baseSelectedProductFormData?.sponsorTriggerEnd ?? "No. of days before expected delivery date",
  sponsorTriggerEndValue: baseFormData?.sponsorTriggerEndValue ?? baseSelectedProductFormData?.sponsorTriggerEndValue ?? "10",
  cmoTriggerStart: baseFormData?.cmoTriggerStart ?? baseSelectedProductFormData?.cmoTriggerStart ?? "PO Status",
  cmoTriggerStartValue: baseFormData?.cmoTriggerStartValue ?? baseSelectedProductFormData?.cmoTriggerStartValue ?? "Confirmed",
  cmoTriggerEnd: baseFormData?.cmoTriggerEnd ?? baseSelectedProductFormData?.cmoTriggerEnd ?? "No. of days before expected delivery date",
  cmoTriggerEndValue: baseFormData?.cmoTriggerEndValue ?? baseSelectedProductFormData?.cmoTriggerEndValue ?? "10",
  allowMultipleLotAllocation: baseFormData?.allowMultipleLotAllocation ?? baseSelectedProductFormData?.allowMultipleLotAllocation ?? "NO",
  allowOverAllocation: baseFormData?.allowOverAllocation ?? baseSelectedProductFormData?.allowOverAllocation ?? "YES",
  overAllocationLimit: baseFormData?.overAllocationLimit ?? baseSelectedProductFormData?.overAllocationLimit ?? "50",
  allowUnderAllocation: baseFormData?.allowUnderAllocation ?? baseSelectedProductFormData?.allowUnderAllocation ?? "NO",
  underAllocationLimit: baseFormData?.underAllocationLimit ?? baseSelectedProductFormData?.underAllocationLimit ?? "10",
  }});

  // Track component mounted state
  const isMountedRef = useRef(true);
  // Track if user is actively typing to prevent overwriting
  const isUserTypingRef = useRef(false);
  // Track timeout for typing detection
  const typingTimeoutRef = useRef(null);
  // Track previous formData to prevent unnecessary updates
  const prevFormDataRef = useRef(null);



  // Handle component unmounting
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Clear any pending typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);
  
  // Note: Initial validation is handled by the stepData validation effect
  // when stepData is first set, so no need for a separate mount effect

  // Initialize apiPoStatuses from formData if available
  useEffect(() => {
    if (formData?.fgStatuses?.poStatuses && apiPoStatuses.length === 0) {
      const poStatusesFromFormData = formData.fgStatuses.poStatuses.filter((status) => status.value === true);
      if (poStatusesFromFormData.length > 0) {
        setApiPoStatuses(poStatusesFromFormData);
      }
    }
  }, [formData?.fgStatuses?.poStatuses, apiPoStatuses.length]);


    // API base URL - replace with your actual API base URL
    const API_BASE_URL = useMemo(() => {
      const appName = localStorage.getItem("appName");
      const network = localStorage.getItem("network");
      const collabType = localStorage.getItem("collabType");
      const roleName = localStorage.getItem("roleName");
  
      if (appName && network && collabType && roleName) {
        return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
      }
      return null;
    }, [
      localStorage.getItem("appName"),
      localStorage.getItem("network"),
      localStorage.getItem("collabType"),
      localStorage.getItem("roleName"),
    ]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  

  // Add effect to handle edit mode data updates - only fetch on first edit
  useEffect(() => {
    // Only fetch data when entering edit mode for the first time and if we don't have data already
    const shouldFetchData = isAllocationConfigEditMode && 
                           formData?.productDetails?.collabrixProductID && 
                           (!formData?.allocationConfiguration || Object.keys(formData?.allocationConfiguration).length === 0);
    
    if (shouldFetchData) {
      const fetchData = async () => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/fetchProductDetails/${formData.productDetails.collabrixProductID}`
          );
          if (isMountedRef.current && response.data && response.data.status) {
            const apiData = response.data.data;
            setUpdateModeData(apiData);
            
            // Set the API poStatuses for dropdown options
            if (apiData?.productFGStatuses?.fgStatusesJSON?.poStatuses) {
              setApiPoStatuses(apiData.productFGStatuses.fgStatusesJSON.poStatuses.filter((status) => status.activated === true));
            }
            
            if (apiData.allocationConfiguration) {
              const poStatus = apiData?.productFGStatuses?.fgStatusesJSON?.poStatuses?.filter((status) => status.activated === true) || 
                             formData?.fgStatuses?.poStatuses?.filter((status) => status.value === true);
              
              // Normalize the allocation configuration using the denormalizeAllocationConfig function
              const normalizedConfig = denormalizeAllocationConfig(apiData.allocationConfiguration, poStatus);
              
              setStepData(prevData => ({
                ...prevData,
                ...normalizedConfig
              }));
              
              // Store the current data to avoid infinite loops
              const configToUpdate = { ...normalizedConfig };
              
              // Use requestAnimationFrame instead of setTimeout to avoid render issues
              requestAnimationFrame(() => {
                if (typeof updateStepData === "function" && isMountedRef.current) {
                  updateStepData("allocationConfiguration", configToUpdate);
                }
              });
            }
          }
        } catch (error) {
          if (isMountedRef.current) {
            setSnackbar({
              open: true,
              message: "Failed to fetch product details.",
              severity: "error",
            });
          }
        }
      };
      fetchData();
    }
  }, [isAllocationConfigEditMode, formData?.productDetails?.collabrixProductID]);

  useEffect(() => {
    let isMounted = true; 
    const collabrixProductID = formData?.productDetails?.collabrixProductID;
    if (editPendingStatus && collabrixProductID) {
      const callData = async () => {
        try {
          setDataLoaded(true); // Add loading state
          const response = await axios.get(
            `${API_BASE_URL}/fetchProductDetails/${collabrixProductID}`
          );
          if (isMounted && response.data && response.data.status) {
            const apiData = response.data.data;
            const poStatus = apiData?.productFGStatuses?.fgStatusesJSON?.poStatuses?.filter((status) => status.activated === true) || 
                           formData?.fgStatuses?.poStatuses?.filter((status) => status.value === true);
            
            // Set the API poStatuses for dropdown options
            if (apiData?.productFGStatuses?.fgStatusesJSON?.poStatuses) {
              setApiPoStatuses(apiData.productFGStatuses.fgStatusesJSON.poStatuses.filter((status) => status.activated === true));
            }
            
            apiData.allocationConfiguration = denormalizeAllocationConfig(apiData.allocationConfiguration, poStatus);
            setUpdateModeData(apiData);
            const allocationConfiguration = apiData.allocationConfiguration || {};
            setStepData(prevData => {
              const updatedData = {
              ...prevData,
              showExpiredLots: allocationConfiguration.showExpiredLots ?? prevData.showExpiredLots,
              defaultAllocationReq: allocationConfiguration.defaultAllocationReq ?? prevData.defaultAllocationReq,
              qtyVarianceAcceptanceLimit: allocationConfiguration.qtyVarianceAcceptanceLimit ?? prevData.qtyVarianceAcceptanceLimit,
              allowLotAllocationBy: allocationConfiguration.allowLotAllocationBy ?? prevData.allowLotAllocationBy,
              holdTimeBtnCreatedAndSWCMO: allocationConfiguration.holdTimeBtnCreatedAndSWCMO ?? prevData.holdTimeBtnCreatedAndSWCMO,
              sponsorTriggerStart: allocationConfiguration.sponsorTriggerStart ?? prevData.sponsorTriggerStart,
              sponsorTriggerStartValue: allocationConfiguration.sponsorTriggerStartValue ?? prevData.sponsorTriggerStartValue,
              sponsorTriggerEnd: allocationConfiguration.sponsorTriggerEnd ?? prevData.sponsorTriggerEnd,
              sponsorTriggerEndValue: allocationConfiguration.sponsorTriggerEndValue ?? prevData.sponsorTriggerEndValue,
              cmoTriggerStart: allocationConfiguration.cmoTriggerStart ?? prevData.cmoTriggerStart,
              cmoTriggerStartValue: allocationConfiguration.cmoTriggerStartValue ?? prevData.cmoTriggerStartValue,
              cmoTriggerEnd: allocationConfiguration.cmoTriggerEnd ?? prevData.cmoTriggerEnd,
              cmoTriggerEndValue: allocationConfiguration.cmoTriggerEndValue ?? prevData.cmoTriggerEndValue,
              allowMultipleLotAllocation: allocationConfiguration.allowMultipleLotAllocation ?? prevData.allowMultipleLotAllocation,
              allowOverAllocation: allocationConfiguration.allowOverAllocation ?? prevData.allowOverAllocation,
              overAllocationLimit: allocationConfiguration.overAllocationLimit ?? prevData.overAllocationLimit,
              allowUnderAllocation: allocationConfiguration.allowUnderAllocation ?? prevData.allowUnderAllocation,
              underAllocationLimit: allocationConfiguration.underAllocationLimit ?? prevData.underAllocationLimit,
              };
              // Store the current data to avoid infinite loops
              const dataToUpdate = { ...updatedData };
              
              // Use requestAnimationFrame instead of setTimeout to avoid render issues
              requestAnimationFrame(() => {
                if (typeof updateStepData === "function" && isMountedRef.current) {
                  updateStepData("allocationConfiguration", dataToUpdate);
                }
              });
              return updatedData;
            });
          } else if (isMounted) {
            setErrors({});
          }
        } catch (error) {
          if (isMountedRef.current) setErrors({});
        } finally {
          if (isMounted) {
            setDataLoaded(false); // Reset loading state
          }
        }
      };
      if(!readOnly) {
        callData();
      }
    }
    return () => { isMounted = false; };
  }, [editPendingStatus, formData?.productDetails?.collabrixProductID]);

  // Update stepData when formData changes and we're not in edit mode
  // Skip sync if user is actively typing to prevent overwriting their input
  useEffect(() => {
    if (formData && formData?.allocationConfiguration && !isAllocationConfigEditMode && !isUserTypingRef.current) {
      // Check if formData has actually changed to prevent unnecessary updates
      const currentFormDataStr = JSON.stringify(formData.allocationConfiguration);
      if (prevFormDataRef.current === currentFormDataStr) {
        return;
      }
      prevFormDataRef.current = currentFormDataStr;
      
      const base = formData?.allocationConfiguration || {};
      const updatedData = {
        ...formData?.allocationConfiguration,
        showExpiredLots: base?.showExpiredLots,
        defaultAllocationReq: base?.defaultAllocationReq,
        qtyVarianceAcceptanceLimit: base?.qtyVarianceAcceptanceLimit,
        allowLotAllocationBy: base?.allowLotAllocationBy, 
        holdTimeBtnCreatedAndSWCMO: base?.holdTimeBtnCreatedAndSWCMO,
        sponsorTriggerStart: base?.sponsorTriggerStart,
        sponsorTriggerStartValue: base?.sponsorTriggerStartValue,
        sponsorTriggerEnd: base?.sponsorTriggerEnd,
        sponsorTriggerEndValue: base?.sponsorTriggerEndValue,
        cmoTriggerStart: base?.cmoTriggerStart, 
        cmoTriggerStartValue: base?.cmoTriggerStartValue,
        cmoTriggerEnd: base?.cmoTriggerEnd,
        cmoTriggerEndValue: base?.cmoTriggerEndValue,
        allowMultipleLotAllocation: base?.allowMultipleLotAllocation,
        allowOverAllocation: base?.allowOverAllocation,
        overAllocationLimit: base?.overAllocationLimit,
        allowUnderAllocation: base?.allowUnderAllocation,
        underAllocationLimit: base?.underAllocationLimit,
      };
      setStepData(updatedData);
    }
  }, [formData, isAllocationConfigEditMode]);

  // Validation function - not using useCallback to avoid circular dependency
  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    // Make sure stepData exists before validating
    if (!stepData) {
      return { isValid: false, errors: { general: "Form data is missing" } };
    }

    if (!stepData.qtyVarianceAcceptanceLimit && stepData.qtyVarianceAcceptanceLimit !== 0) {
      newErrors.qtyVarianceAcceptanceLimit = "Batch consumption qty. variance acceptance absolute limit is required";
      isValid = false;
    } else if (isNaN(stepData.qtyVarianceAcceptanceLimit)) {
      newErrors.qtyVarianceAcceptanceLimit = "Batch consumption qty. variance acceptance absolute limit must be a number";
      isValid = false;
    } else if (Number(stepData.qtyVarianceAcceptanceLimit) < 0) {
      newErrors.qtyVarianceAcceptanceLimit = "Value cannot be negative";
      isValid = false;
    } 

    // Sponsor Trigger Start Value validation (skip if CMO only)
    if (
      stepData.sponsorTriggerStart &&
      stepData.sponsorTriggerStart !== 'PO Status' &&
      stepData.sponsorTriggerStart !== 'NA' &&
      !isCMOOnly &&
      (!stepData.sponsorTriggerStartValue || stepData.sponsorTriggerStartValue.toString().trim() === "")
    ) {
      newErrors.sponsorTriggerStartValue = "Batch allocation start trigger by sponsor value is required";
      isValid = false;
    }

    // Sponsor Trigger End Value validation (skip if CMO only)
    if (
      stepData.sponsorTriggerEnd &&
      stepData.sponsorTriggerEnd !== 'PO Status' &&
      stepData.sponsorTriggerEnd !== 'NA' &&
      !isCMOOnly &&
      (!stepData.sponsorTriggerEndValue || stepData.sponsorTriggerEndValue.toString().trim() === "")
    ) {
      newErrors.sponsorTriggerEndValue = "Batch allocation end trigger by sponsor value is required";
      isValid = false;
    }

    // CMO Trigger Start Value validation
    if (
      stepData.cmoTriggerStart &&
      stepData.cmoTriggerStart !== 'PO Status' &&
      stepData.cmoTriggerStart !== 'NA' &&
      (!stepData.cmoTriggerStartValue || stepData.cmoTriggerStartValue.toString().trim() === "")
    ) {
      newErrors.cmoTriggerStartValue = "Batch allocation start trigger by CMO value is required";
      isValid = false;
    }

    // CMO Trigger End Value validation
    if (
      stepData.cmoTriggerEnd &&
      stepData.cmoTriggerEnd !== 'PO Status' &&
      stepData.cmoTriggerEnd !== 'NA' &&
      (!stepData.cmoTriggerEndValue || stepData.cmoTriggerEndValue.toString().trim() === "")
    ) {
      newErrors.cmoTriggerEndValue = "Batch allocation end trigger by CMO value is required";
      isValid = false;
    }

    setErrors(newErrors);
    return { isValid, errors: newErrors };
  };

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    let safeValue = value || "";

    // Mark that user is actively typing
    isUserTypingRef.current = true;
    
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Clear the typing flag after a short delay
    typingTimeoutRef.current = setTimeout(() => {
      isUserTypingRef.current = false;
    }, 1000); // Increased timeout to give user more time

    const newErrors = { ...errors };
    if (name === 'qtyVarianceAcceptanceLimit') {
      if (!safeValue && safeValue !== 0) {
        newErrors.qtyVarianceAcceptanceLimit = "Batch consumption variance acceptance absolute limit is required";
      } else if (isNaN(safeValue)) {
        newErrors.qtyVarianceAcceptanceLimit = "Batch consumption variance acceptance absolute limit must be a number";
      } else if (Number(safeValue) < 0) {
        newErrors.qtyVarianceAcceptanceLimit = "Value cannot be negative";
      } else {
        delete newErrors.qtyVarianceAcceptanceLimit;
      }
    }
    if (stepData.sponsorTriggerStart !== 'PO Status' && !isCMOOnly && name === 'sponsorTriggerStartValue') {
      if (!safeValue || safeValue.toString().trim() === "") {
        newErrors.sponsorTriggerStartValue = "Sponsor start trigger value is required";
      } else {
        delete newErrors.sponsorTriggerStartValue;
      }
    }
    if (stepData.sponsorTriggerEnd !== 'PO Status' && !isCMOOnly && name === 'sponsorTriggerEndValue') {
      if (!safeValue || safeValue.toString().trim() === "") {
        newErrors.sponsorTriggerEndValue = "Sponsor end trigger value is required";
      } else {
        delete newErrors.sponsorTriggerEndValue;
      }
    }
    if (stepData.cmoTriggerStart !== 'PO Status' &&  name === 'cmoTriggerStartValue') {
      if (!safeValue || safeValue.toString().trim() === "") {
        newErrors.cmoTriggerStartValue = "CMO start trigger value is required";
      } else {
        delete newErrors.cmoTriggerStartValue;
      }
    }
    if (stepData.cmoTriggerEnd !== 'PO Status' &&  name === 'cmoTriggerEndValue') {
      if (!safeValue || safeValue.toString().trim() === "") {
        newErrors.cmoTriggerEndValue = "CMO end trigger value is required";
      } else {
        delete newErrors.cmoTriggerEndValue;
      }
    } 

    setErrors(newErrors);
    
    // Reset trigger values when trigger type changes
    let updatedStepData = {
      ...stepData,
      [name]: safeValue,
    };

    // Reset sponsor trigger start value when sponsor trigger start type changes
    if (name === 'sponsorTriggerStart') {
      updatedStepData.sponsorTriggerStartValue = "";
    }
    
    // Reset sponsor trigger end value when sponsor trigger end type changes
    if (name === 'sponsorTriggerEnd') {
      updatedStepData.sponsorTriggerEndValue = "";
    }
    
    // Reset CMO trigger start value when CMO trigger start type changes
    if (name === 'cmoTriggerStart') {
      updatedStepData.cmoTriggerStartValue = "";
    }
    
    // Reset CMO trigger end value when CMO trigger end type changes
    if (name === 'cmoTriggerEnd') {
      updatedStepData.cmoTriggerEndValue = "";
    }

    // Store the current data to avoid infinite loops
    const dataToUpdate = { ...updatedStepData };
    
    setStepData(dataToUpdate);
    
    // Use requestAnimationFrame instead of setTimeout to avoid render issues
    requestAnimationFrame(() => {
      if (typeof updateStepData === "function" && isMountedRef.current) {
        updateStepData("allocationConfiguration", dataToUpdate);
      }
    });
    
    // Note: Validation is handled by the useEffect that runs when stepData changes
    // This prevents circular dependency issues
  }, [errors, stepData, updateStepData]);

  const saveAllocationsConfigurations = async () => {
    function normalizeAllocationConfig(data, poStatus) {
      return {
        showExpiredLots:data.showExpiredLots === "YES",
        defaultAllocationReq: data.defaultAllocationReq === "YES",
        qtyVarianceAcceptanceLimit: Number(data.qtyVarianceAcceptanceLimit),
        allowMultipleLotAllocation: data.allowMultipleLotAllocation === "YES",
        allowOverAllocation: data.allowOverAllocation === "NA" ? undefined : data.allowOverAllocation === "YES",
        overAllocationLimit: data.overAllocationLimit === "NA" ? undefined : Number(data.overAllocationLimit),
        holdTimeBtnCreatedAndSWCMO: data.holdTimeBtnCreatedAndSWCMO === "NA" ? undefined : Number(data.holdTimeBtnCreatedAndSWCMO),
        allowLotAllocationBy: data.allowLotAllocationBy === "Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)" ? "all": data.allowLotAllocationBy, 
        sponsorTriggerStart: data.sponsorTriggerStart === "NA" ? undefined : data.sponsorTriggerStart === "PO Status" ? "poStatus": data.sponsorTriggerStart === "No. of days before scheduled manufacturing start date" ? "scheduleMfgStartDateBefore": "expectedDeliveryDateBefore",
        sponsorTriggerEnd: data.sponsorTriggerEnd === "NA" ? undefined : data.sponsorTriggerEnd === "PO Status" ? "poStatus": data.sponsorTriggerEnd === "No. of days before scheduled manufacturing start date" ? "scheduleMfgStartDateBefore": "expectedDeliveryDateBefore",
        cmoTriggerStart: data.cmoTriggerStart === "NA" ? undefined : data.cmoTriggerStart === "PO Status" ? "poStatus": data.cmoTriggerStart === "No. of days before scheduled manufacturing start date" ? "scheduleMfgStartDateBefore": "expectedDeliveryDateBefore",
        cmoTriggerEnd: data.cmoTriggerEnd === "NA" ? undefined : data.cmoTriggerEnd === "PO Status" ? "poStatus": data.cmoTriggerEnd === "No. of days before scheduled manufacturing start date" ? "scheduleMfgStartDateBefore": "expectedDeliveryDateBefore",
        sponsorTriggerStartValue: data.sponsorTriggerStartValue === "NA" ? undefined : data.sponsorTriggerStart === "PO Status" ? (poStatus?.find((status) => status.status_name.toLowerCase() === (typeof data.sponsorTriggerStartValue === 'string' ? data.sponsorTriggerStartValue.toLowerCase() : data.sponsorTriggerStartValue))?.status_id ?? undefined) : Number(data.sponsorTriggerStartValue),
        sponsorTriggerEndValue: data.sponsorTriggerEndValue === "NA" ? undefined : data.sponsorTriggerEnd === "PO Status" ? (poStatus?.find((status) => status.status_name.toLowerCase() === (typeof data.sponsorTriggerEndValue === 'string' ? data.sponsorTriggerEndValue.toLowerCase() : data.sponsorTriggerEndValue))?.status_id ?? undefined) : Number(data.sponsorTriggerEndValue),
        cmoTriggerStartValue: data.cmoTriggerStartValue === "NA" ? undefined : data.cmoTriggerStart === "PO Status" ? (poStatus?.find((status) => status.status_name.toLowerCase() === (typeof data.cmoTriggerStartValue === 'string' ? data.cmoTriggerStartValue.toLowerCase() : data.cmoTriggerStartValue))?.status_id ?? undefined) : Number(data.cmoTriggerStartValue),
        cmoTriggerEndValue: data.cmoTriggerEndValue === "NA" ? undefined : data.cmoTriggerEnd === "PO Status" ? (poStatus?.find((status) => status.status_name.toLowerCase() === (typeof data.cmoTriggerEndValue === 'string' ? data.cmoTriggerEndValue.toLowerCase() : data.cmoTriggerEndValue))?.status_id ?? undefined) : Number(data.cmoTriggerEndValue),
        // Under allocation fields - independent of multiple lot allocation
        allowUnderAllocation: data.allowUnderAllocation === "NA" ? undefined : data.allowUnderAllocation === "YES",
        allowUnderAllocationLimit: data.underAllocationLimit === "NA" ? undefined : Number(data.underAllocationLimit),
      };
    }

    let dataToSend = { ...stepData };
    const newConfig = normalizeAllocationConfig(dataToSend, poStatus);

    let oldConfig;
    
    if (editPendingStatus && updateModeData?.allocationConfiguration) {
      oldConfig = normalizeAllocationConfig(updateModeData.allocationConfiguration, poStatus);
    } else if (addNewProductConfig && selectedProductFormData?.allocationConfiguration) {
      oldConfig = normalizeAllocationConfig(selectedProductFormData.allocationConfiguration, poStatus);
    } else if (formData.allocationConfiguration) {
      oldConfig = normalizeAllocationConfig(formData.allocationConfiguration, poStatus);
    } else {
      oldConfig = normalizeAllocationConfig({}, poStatus);
    }

const validationResult = validateForm();
if (!validationResult.isValid) {
  const errorMessages = Object.values(validationResult.errors);
  setSnackbar({
    open: true,
    message: errorMessages.join(". "),
    severity: "error",
  });
  return;
}
    
    try {
      if (isMountedRef.current) {
        setIsLoading(true);
      }
      setIsLoading(true);
      setErrors({});

      // Helper function to format productDetails for API
      const formatProductDetailsForAPI = (productDetails) => {
        const formatted = { ...productDetails };
        
        // Convert receivingStorageLocationIDsFromSponsorERP from array to comma-separated string
        if (formatted.receivingStorageLocationIDsFromSponsorERP) {
          if (Array.isArray(formatted.receivingStorageLocationIDsFromSponsorERP)) {
            formatted.receivingStorageLocationIDsFromSponsorERP = 
              formatted.receivingStorageLocationIDsFromSponsorERP.join(",");
          }
        }
        
        return formatted;
      };

      const payload = {
        updateAllocationConfigFlag: true,
        productDetails: editPendingStatus && updateModeData
          ? formatProductDetailsForAPI({
            ...updateModeData.productDetails,
            collabrixProductID: updateModeData.productDetails?.collabrixProductID || selectedCollabrixID,
            vendorMosymphonyID: updateModeData.productDetails?.vendorMosymphonyID,
            pendingConfiguration: true,
          })
          : formatProductDetailsForAPI({
            ...formData.productDetails,
            collabrixProductID: formData.productDetails?.collabrixProductID,
            vendorMosymphonyID: formData.productDetails?.vendorMosymphonyID,
            pendingConfiguration: true,
          }),
        allocationConfiguration : newConfig
      };
     
      const response = await axios.post(
        `${API_BASE_URL}/updateProductDetails`,
        payload
      );      
      
      if (typeof markApiCallSuccessful === 'function' && response.data && response.data.status) {
        markApiCallSuccessful();
        return;
      }

      if (response.data && response.data.status) {
        handleNext();
        if (isMountedRef.current) {
          setSnackbar({
            open: true,
            message: "Allocation configurations saved successfully!",
            severity: "success",
          });
        }
      } else {
        if (isMountedRef.current) {
          setSnackbar({
            open: true,
            message: "Failed to save Allocation configurations. Please try again.",
            severity: "error",
          });
        }
      }
    } catch (error) {
      const errorMessage = "An error occurred while saving: " + (error.message || "Unknown error");
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }


  const poStatus = apiPoStatuses.length > 0 ? apiPoStatuses : formData?.fgStatuses?.poStatuses?.filter((status) => status.value === true);
  const productType = formData?.productDetails?.productTypeID

   const productTypeName = formData?.productDetails?.productTypeName
// const materialTypeName = formData?.materialDetails ? formData?.materialDetails?.productMaterialsList[0]?.materialTypeName : ""
const materialTypeName =
  Array.isArray(formData?.materialDetails?.productMaterialsList) &&
  formData.materialDetails.productMaterialsList.length > 0
    ? formData.materialDetails.productMaterialsList[0].materialTypeName
    : "";

// Over-allocation restriction removed - can now be done for all product-material type combinations
// const isOverAllocationAllowed = () => {
//   const product = (productTypeName || "").toLowerCase();
//   const material = (materialTypeName || "").toLowerCase();
//   return (
//     (product.includes("fg") && material.includes("bulk dp")) ||
//     (product.includes("bnu") && material.includes("bulk dp")) ||
//     (product.includes("fg") && material.includes("vial")) ||
//     (product.includes("fg") && material.includes("bnu"))
//   );
// };

  const isSponsorOnly = stepData.allowLotAllocationBy === "Sponsor";
  const isCMOOnly = stepData.allowLotAllocationBy === "CMO";
  const isBoth = stepData.allowLotAllocationBy === "Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)";

useEffect(() => {

  if (readOnly) {
    return
  }

  const updatedData = { ...stepData };
  let hasChanges = false;

  if (isSponsorOnly) {
    if (updatedData.cmoTriggerStart !== "NA") {
      updatedData.cmoTriggerStart = "NA";
      updatedData.cmoTriggerStartValue = "NA";
      hasChanges = true;
    }
    if (updatedData.cmoTriggerEnd !== "NA") {
      updatedData.cmoTriggerEnd = "NA";
      updatedData.cmoTriggerEndValue = "NA";
      hasChanges = true;
    }
    // Set specific values for sponsor triggers when Sponsor is selected
    if (updatedData.sponsorTriggerStart !== "PO Status") {
      updatedData.sponsorTriggerStart = "PO Status";
      updatedData.sponsorTriggerStartValue = "Created";
      hasChanges = true;
    }
    if (updatedData.sponsorTriggerEnd !== "No. of days before expected delivery date") {
      updatedData.sponsorTriggerEnd = "No. of days before expected delivery date";
      updatedData.sponsorTriggerEndValue = "10";
      hasChanges = true;
    }
  } else {
    // Reset to default values when CMO is enabled
    if (updatedData.cmoTriggerStart === "NA") {
      updatedData.cmoTriggerStart = "PO Status";
      updatedData.cmoTriggerStartValue = "Confirmed";
      hasChanges = true;
    }
    if (updatedData.cmoTriggerEnd === "NA") {
      updatedData.cmoTriggerEnd = "No. of days before expected delivery date";
      updatedData.cmoTriggerEndValue = "10";
      hasChanges = true;
    }
  }

  if (isCMOOnly) {
    if (updatedData.sponsorTriggerStart !== "NA") {
      updatedData.sponsorTriggerStart = "NA";
      updatedData.sponsorTriggerStartValue = "NA";
      hasChanges = true;
    }
    if (updatedData.sponsorTriggerEnd !== "NA") {
      updatedData.sponsorTriggerEnd = "NA";
      updatedData.sponsorTriggerEndValue = "NA";
      hasChanges = true;
    }
  } else {
    // Reset to default values when Sponsor is enabled
    if (updatedData.sponsorTriggerStart === "NA") {
      updatedData.sponsorTriggerStart = "PO Status";
      updatedData.sponsorTriggerStartValue = "Created";
      hasChanges = true;
    }
    if (updatedData.sponsorTriggerEnd === "NA") {
      updatedData.sponsorTriggerEnd = "No. of days before expected delivery date";
      updatedData.sponsorTriggerEndValue = "10";
      hasChanges = true;
    }
  }

  // Special handling for "Both Sponsor and CMO" selection - set specific values
  if (isBoth) {
    // Set sponsor start trigger defaults only if not already set properly
    if (updatedData.sponsorTriggerStart !== "PO Status" || !updatedData.sponsorTriggerStartValue) {
      updatedData.sponsorTriggerStart = "PO Status";
      updatedData.sponsorTriggerStartValue = updatedData.sponsorTriggerStartValue || "Created";
      hasChanges = true;
    }
    
    // For sponsor end trigger, only set defaults if not already set to PO Status
    // or if the current value doesn't make sense for the current trigger type
    if (updatedData.sponsorTriggerEnd !== "PO Status") {
      // If changing from days to PO Status, set a default CMO-related status
      updatedData.sponsorTriggerEnd = "PO Status";
      
      // Find a suitable CMO-related status from available options
      const cmoRelatedStatuses = poStatus?.filter(status => 
        status.status_name.toLowerCase().includes("shared") ||
        status.status_name.toLowerCase().includes("cmo") ||
        status.status_name.toLowerCase() === "confirmed"
      ) || [];
      
      const sharedWithCMOStatus = poStatus?.find(status => 
        status.status_name.toLowerCase() === "shared with cmo"
      );
      
      // Prefer "Shared with CMO" if available, otherwise use first CMO-related status, otherwise keep current value
      if (sharedWithCMOStatus) {
        updatedData.sponsorTriggerEndValue = sharedWithCMOStatus.status_name;
      } else if (cmoRelatedStatuses.length > 0) {
        updatedData.sponsorTriggerEndValue = cmoRelatedStatuses[0].status_name;
      }
      // If no CMO-related status found, keep the current value as user might have set it intentionally
      
      hasChanges = true;
    } else if (updatedData.sponsorTriggerEnd === "PO Status" && 
               updatedData.sponsorTriggerEndValue && 
               !isNaN(updatedData.sponsorTriggerEndValue)) {
      // If trigger is PO Status but value is a number (like "10"), suggest a status instead
      const sharedWithCMOStatus = poStatus?.find(status => 
        status.status_name.toLowerCase() === "shared with cmo"
      );
      
      if (sharedWithCMOStatus) {
        updatedData.sponsorTriggerEndValue = sharedWithCMOStatus.status_name;
        hasChanges = true;
      }
    }
  }

  if (isSponsorOnly) {
    if (updatedData.holdTimeBtnCreatedAndSWCMO !== "0") {
      updatedData.holdTimeBtnCreatedAndSWCMO = "0";
      hasChanges = true;
    }
  } else {
    if (
      updatedData.holdTimeBtnCreatedAndSWCMO === "0" ||
      updatedData.holdTimeBtnCreatedAndSWCMO === "NA" ||
      !updatedData.holdTimeBtnCreatedAndSWCMO 
    ) {
      updatedData.holdTimeBtnCreatedAndSWCMO = "12";
      hasChanges = true;
    }
  }

  // Over-allocation and under-allocation are now independent of multiple lot allocation
  // Over allocation logic - independent of multiple lot allocation
  if (stepData.allowOverAllocation === "NO" /* || !isOverAllocationAllowed() */) {
    if (updatedData.overAllocationLimit !== "NA") {
      updatedData.overAllocationLimit = "NA";
      hasChanges = true;
    }
  } else {
    // Reset to default value when over allocation limit is enabled
    if (updatedData.overAllocationLimit === "NA" && updatedData.allowOverAllocation === "YES") {
      updatedData.overAllocationLimit = "50";
      hasChanges = true;
    }
  }

  // Under allocation logic - independent of multiple lot allocation
  if (stepData.allowUnderAllocation === "NO") {
    if (updatedData.underAllocationLimit !== "NA") {
      updatedData.underAllocationLimit = "NA";
      hasChanges = true;
    }
  } else {
    // Reset to default value when under allocation limit is enabled
    if (updatedData.underAllocationLimit === "NA" && updatedData.allowUnderAllocation === "YES") {
      updatedData.underAllocationLimit = "10";
      hasChanges = true;
    }
  }

  if (hasChanges) {
    // Store the current data to avoid infinite loops
    const dataToUpdate = { ...updatedData };
    
    setStepData(dataToUpdate);
    
    // Use requestAnimationFrame instead of setTimeout to avoid render issues
    requestAnimationFrame(() => {
      if (typeof updateStepData === "function" && isMountedRef.current) {
        updateStepData("allocationConfiguration", dataToUpdate);
      }
    });
  }
}, [isSponsorOnly, isCMOOnly, isBoth, productType]);

  // Validation effect - runs validation when stepData changes
  // Using a ref to prevent infinite loops with setTimeout
  const prevValidationRef = useRef(null);
  
  useEffect(() => {
    if (typeof onValidationChange === 'function') {
      const validationResult = validateForm();
      
      // Only update if validation result has changed
      const validationString = JSON.stringify(validationResult);
      if (prevValidationRef.current !== validationString) {
        prevValidationRef.current = validationString;
        
        // Use setTimeout to avoid updating parent during render
        setTimeout(() => {
          onValidationChange(validationResult.isValid);
        }, 0);
      }
    }
  }, [stepData]);


  return (
    <div className={classes.formContainer}>
      {/* Full Page Loader */}
      {dataLoaded && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}

      <form>
        <Typography variant="h6" className={classes?.formTitle}>
          Allocation Configurations
        </Typography>
        <Grid className="two-col-forms-grid" container spacing={3}>
          {/* Row 1 */}
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="showExpiredLots">
                Show expired materials in batch allocation
              </Typography>
              {!readOnly ? (
                <Select
                  id="showExpiredLots"
                  name="showExpiredLots"
                  value={stepData.showExpiredLots}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="YES">YES</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </Select>
              ) : (
                <Typography>{stepData.showExpiredLots}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="defaultAllocationReq">
                Default batch allocation req
              </Typography>
              {!readOnly ? (
                <Select
                  id="defaultAllocationReq"
                  name="defaultAllocationReq"
                  value={stepData.defaultAllocationReq}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="YES">YES</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </Select>
              ) : (
                <Typography>{stepData.defaultAllocationReq}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Row 2 */}
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="qtyVarianceAcceptanceLimit">
                Batch consumption qty. variance acceptance absolute limit (± %) {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {!readOnly ? (
                <TextField
                  id="qtyVarianceAcceptanceLimit"
                  name="qtyVarianceAcceptanceLimit"
                  value={stepData.qtyVarianceAcceptanceLimit}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  error={!!errors.qtyVarianceAcceptanceLimit}
                  helperText={errors.qtyVarianceAcceptanceLimit}
                  InputProps={{
                    style: commonInlineStyles.input
                  }}
                  inputProps={{
                    ...commonInlineStyles.inputProps,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                />
              ) : (
                <Typography>{stepData.qtyVarianceAcceptanceLimit}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Empty space for grid alignment */}
          <Grid item xs={6}>
            {/* Intentionally empty */}
          </Grid>

          </Grid>
          {/* Row 3 */}
          <Grid className="two-col-forms-grid" container spacing={3} style={{ margin: "50px 0px", padding: "25px", border: "2px solid #B4B4B4", gap: "50px 250px" }}>
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="allowLotAllocationBy">
                Allow batch allocation by
              </Typography>
              {!readOnly ? (
                <Select
                  id="allowLotAllocationBy"
                  name="allowLotAllocationBy"
                  value={stepData.allowLotAllocationBy}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="Sponsor">Sponsor</MenuItem>
                  <MenuItem value="CMO">CMO</MenuItem>
                  <MenuItem value="Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)">
                    Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)
                  </MenuItem>
                </Select>
              ) : (
                <Typography>{stepData.allowLotAllocationBy}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography 
                component="label" 
                htmlFor="holdTimeBtnCreatedAndSWCMO"
                style={{ color: isSponsorOnly  ? "#999" : "inherit" }}
              >
                Specify hold time between Created and Shared with CMO PO status (working hours)
              </Typography>
              {!readOnly ? (
                <TextField
                  id="holdTimeBtnCreatedAndSWCMO"
                  name="holdTimeBtnCreatedAndSWCMO"
                  value={isSponsorOnly ? "0" : stepData.holdTimeBtnCreatedAndSWCMO}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  disabled={isSponsorOnly}
                  InputProps={{
                    style: commonInlineStyles.input,
                    onBeforeInput: (e) => {
                    const allowedChars = /^[0-9]+$/;
                    if (!allowedChars.test(e.data)) {
                      e.preventDefault();
                    }
                  }
                  }}
                  inputProps={commonInlineStyles.inputProps}
                />
              ) : (
                <Typography>{isSponsorOnly ? "0" : stepData.holdTimeBtnCreatedAndSWCMO}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Row 4 - Sponsor triggers */}
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="sponsorTriggerStart" style={{ color: isCMOOnly || isBoth ? "#999" : "inherit" }}>
                Batch allocation start trigger by sponsor
              </Typography>
              {!readOnly ? (
                <Select
                  id="sponsorTriggerStart"
                  name="sponsorTriggerStart"
                  value={isCMOOnly ? "NA" : stepData.sponsorTriggerStart}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isCMOOnly || isBoth}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  {isCMOOnly && <MenuItem value="NA">NA</MenuItem>}
                  <MenuItem value="PO Status">PO Status</MenuItem>
                  <MenuItem value="No. of days before scheduled manufacturing start date">No. of days before scheduled manufacturing start date</MenuItem>                
                  <MenuItem value="No. of days before expected delivery date">No. of days before expected delivery date</MenuItem>
                </Select>
              ) : (
                <Typography>{isCMOOnly ? "NA" : stepData.sponsorTriggerStart}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="sponsorTriggerStartValue" style={{ color: isCMOOnly || isBoth ? "#999" : "inherit" }}>
                Batch allocation start trigger by sponsor value {!(isCMOOnly || isBoth) && stepData.sponsorTriggerStart !== "PO Status" && !readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {!readOnly ? (
                stepData.sponsorTriggerStart === "PO Status"? <Select
                  id="sponsorTriggerStartValue"
                  name="sponsorTriggerStartValue"
                  value={isCMOOnly ? "NA" : stepData.sponsorTriggerStartValue}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isCMOOnly || isBoth}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  {isCMOOnly && <MenuItem value="NA">NA</MenuItem>}
                  {poStatus?.map((item) => <MenuItem value={item.status_name}>{item.status_name}</MenuItem>)}
                </Select> :               
                <TextField
                  id="sponsorTriggerStartValue"
                  name="sponsorTriggerStartValue"
                  value={isCMOOnly ? "NA" : stepData.sponsorTriggerStartValue}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  InputProps={{
                    style: commonInlineStyles.input,
                    onBeforeInput: (e) => {
                    const allowedChars = /^[0-9]+$/;
                    if (!allowedChars.test(e.data)) {
                      e.preventDefault();
                    }
                  }
                  }}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isCMOOnly || isBoth}
                />
              ) : (
                <Typography>{isCMOOnly ? "NA" : stepData.sponsorTriggerStartValue}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Row 5 */}
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="sponsorTriggerEnd" style={{ color: isCMOOnly || isBoth ? "#999" : "inherit" }}>
                Batch allocation end trigger by sponsor
              </Typography>
              {!readOnly ? (
                <Select
                  id="sponsorTriggerEnd"
                  name="sponsorTriggerEnd"
                  value={isCMOOnly ? "NA" : stepData.sponsorTriggerEnd}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isCMOOnly || isBoth}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  {isCMOOnly && <MenuItem value="NA">NA</MenuItem>}
                  <MenuItem value="PO Status">PO Status</MenuItem>
                  <MenuItem value="No. of days before scheduled manufacturing start date">No. of days before scheduled manufacturing start date</MenuItem>
                  <MenuItem value="No. of days before expected delivery date">No. of days before expected delivery date</MenuItem>
                </Select>
              ) : (
                <Typography>{isCMOOnly ? "NA" : stepData.sponsorTriggerEnd}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="sponsorTriggerEndValue" style={{ color: isCMOOnly || isBoth ? "#999" : "inherit" }}>
                Batch allocation end trigger by sponsor value {!(isCMOOnly || isBoth) && stepData.sponsorTriggerEnd !== "PO Status" && !readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {!readOnly ? (
                stepData.sponsorTriggerEnd === "PO Status" ? <Select
                  id="sponsorTriggerEndValue"
                  name="sponsorTriggerEndValue"
                  value={isCMOOnly ? "NA" : stepData.sponsorTriggerEndValue}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isCMOOnly || isBoth}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  {isCMOOnly && <MenuItem value="NA">NA</MenuItem>}
                  {poStatus?.map((item) => <MenuItem value={item.status_name}>{item.status_name}</MenuItem>)}
                </Select> :              
                <TextField
                  id="sponsorTriggerEndValue"
                  name="sponsorTriggerEndValue"
                  value={isCMOOnly ? "NA" : stepData.sponsorTriggerEndValue}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  InputProps={{
                    style: commonInlineStyles.input,
                    onBeforeInput: (e) => {
                    const allowedChars = /^[0-9]+$/;
                    if (!allowedChars.test(e.data)) {
                      e.preventDefault();
                    }
                  }
                  }}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isCMOOnly || isBoth}
                />
              ) : (
                <Typography>{isCMOOnly ? "NA" : stepData.sponsorTriggerEndValue}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Row 6 - CMO triggers */}
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography 
                component="label" 
                htmlFor="cmoTriggerStart"
                style={{ color: isSponsorOnly ? "#999" : "inherit" }}
              >
                Batch allocation start trigger by CMO (only if CMO batch allocation allowed)
              </Typography>
              {!readOnly ? (
                <Select
                  id="cmoTriggerStart"
                  name="cmoTriggerStart"
                  value={isSponsorOnly? "NA" : stepData.cmoTriggerStart}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isSponsorOnly}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                {isSponsorOnly && (
                  <MenuItem value="NA" disabled>
                    NA
                  </MenuItem>
                )}
                  <MenuItem value="PO Status">PO Status</MenuItem>
                  <MenuItem value="No. of days before scheduled manufacturing start date">No. of days before scheduled manufacturing start date</MenuItem>
                  <MenuItem value="No. of days before expected delivery date">No. of days before expected delivery date</MenuItem>
                </Select>
              ) : (
                <Typography>{isSponsorOnly? "NA" : stepData.cmoTriggerStart}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography 
                component="label" 
                htmlFor="cmoTriggerStartValue"
                style={{ color: isSponsorOnly ? "#999" : "inherit" }}
              >
                Batch allocation start trigger by CMO value (only if CMO batch allocation allowed) {!isSponsorOnly && stepData.cmoTriggerStart !== "PO Status" && !readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {!readOnly ? (
                stepData.cmoTriggerStart === "PO Status" ? <Select
                  id="cmoTriggerStartValue"
                  name="cmoTriggerStartValue"
                  value={isSponsorOnly? "NA" : stepData.cmoTriggerStartValue}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isSponsorOnly}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  {isSponsorOnly && <MenuItem value="NA">NA</MenuItem>}
                  {poStatus?.map((item) => <MenuItem value={item.status_name}>{item.status_name}</MenuItem>)}
                </Select> :
                <TextField
                  id="cmoTriggerStartValue"
                  name="cmoTriggerStartValue"
                  value={isSponsorOnly? "NA" : stepData.cmoTriggerStartValue}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  disabled={isSponsorOnly}
                  InputProps={{
                    style: commonInlineStyles.input,
                    onBeforeInput: (e) => {
                    const allowedChars = /^[0-9]+$/;
                    if (!allowedChars.test(e.data)) {
                      e.preventDefault();
                    }
                  }
                  }}
                  inputProps={commonInlineStyles.inputProps}
                />
              ) : (
                <Typography>{isSponsorOnly? "NA" : stepData.cmoTriggerStartValue}</Typography>
              )}
            </FormControl>  
          </Grid>

          {/* Row 7 */}
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography 
                component="label" 
                htmlFor="cmoTriggerEnd"
                style={{ color: isSponsorOnly ? "#999" : "inherit" }}
              >
                Batch allocation end trigger by CMO (only if CMO batch allocation allowed)
              </Typography>
              {!readOnly ? (
                <Select
                  id="cmoTriggerEnd"
                  name="cmoTriggerEnd"
                  value={isSponsorOnly ? "NA" : stepData.cmoTriggerEnd}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isSponsorOnly}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >  
                {isSponsorOnly && (
                  <MenuItem value="NA" disabled>
                    NA
                  </MenuItem>
                )}
                  <MenuItem value="PO Status">PO Status</MenuItem>
                  <MenuItem value="No. of days before scheduled manufacturing start date">No. of days before scheduled manufacturing start date</MenuItem>
                  <MenuItem value="No. of days before expected delivery date">No. of days before expected delivery date</MenuItem>
                </Select>
              ) : (
                <Typography>{isSponsorOnly ? "NA" : stepData.cmoTriggerEnd}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography 
                component="label" 
                htmlFor="cmoTriggerEndValue"
                style={{ color: isSponsorOnly ? "#999" : "inherit" }}
              >
                Batch allocation end trigger by CMO value (only if CMO batch allocation allowed) {!isSponsorOnly && stepData.cmoTriggerEnd !== "PO Status" && !readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {!readOnly ? (
                stepData.cmoTriggerEnd === "PO Status" ? <Select
                  id="cmoTriggerEndValue"
                  name="cmoTriggerEndValue"
                  value={isSponsorOnly? "NA" : stepData.cmoTriggerEndValue}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  disabled={isSponsorOnly}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  {isSponsorOnly && <MenuItem value="NA">NA</MenuItem>}
                  {poStatus?.map((item) => <MenuItem value={item.status_name}>{item.status_name}</MenuItem>)}
                </Select> :
                <TextField
                  id="cmoTriggerEndValue"
                  name="cmoTriggerEndValue"
                  value={isSponsorOnly ? "NA" : stepData.cmoTriggerEndValue}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  disabled={isSponsorOnly}
                  InputProps={{
                    style: commonInlineStyles.input,
                    onBeforeInput: (e) => {
                    const allowedChars = /^[0-9]+$/;
                    if (!allowedChars.test(e.data)) {
                      e.preventDefault();
                    }
                  }
                  }}
                  inputProps={commonInlineStyles.inputProps}
                />
              ) : (
                <Typography>{isSponsorOnly ? "NA" : stepData.cmoTriggerEndValue}</Typography>
              )}
            </FormControl>
          </Grid>
          </Grid>
          {/* Row 8 */}
          <Grid className="two-col-forms-grid" container spacing={3}>
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="allowMultipleLotAllocation">
                Allow multiple material batch allocations
              </Typography>
              {!readOnly ? (
                <Select
                  id="allowMultipleLotAllocation"
                  name="allowMultipleLotAllocation"
                  value={stepData.allowMultipleLotAllocation}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="YES">YES</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </Select>
              ) : (
                <Typography>{stepData.allowMultipleLotAllocation}</Typography>
              )}
            </FormControl>
          </Grid>
            
          {/* Empty space */}
          <Grid item xs={6}>
            {/* Intentionally empty */}
          </Grid>
          {/* Row 10 - Under allocation */}
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="allowUnderAllocation">
                Allow under allocation
              </Typography>
              {!readOnly ? (
                <Select
                  id="allowUnderAllocation"
                  name="allowUnderAllocation"
                  value={stepData.allowUnderAllocation}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="YES">YES</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </Select>
              ) : (
                <Typography>{stepData.allowUnderAllocation}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="underAllocationLimit" style={{color: stepData.allowUnderAllocation === "NO" ? "#999" : "inherit" }}>
                Under allocation limit (%) (only if under allocation is YES)
              </Typography>
              {!readOnly ? (
                <TextField
                  id="underAllocationLimit"
                  name="underAllocationLimit"
                  value={stepData.allowUnderAllocation === "NO" ? "NA" : stepData.underAllocationLimit}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  disabled={stepData.allowUnderAllocation === "NO"}
                  InputProps={{
                    style: commonInlineStyles.input,
                    onBeforeInput: (e) =>  {
                      const v = e.target.value;
                      const nv = v.slice(0, e.target.selectionStart) + e.data + v.slice(e.target.selectionEnd);
                      if (!/^\d+$/.test(e.data) || +nv < 1 || +nv > 100) e.preventDefault();
                    }
                  }}
                  inputProps={commonInlineStyles.inputProps}
                />
              ) : (
                <Typography>{stepData.allowUnderAllocation === "NO" ? "NA" : stepData.underAllocationLimit}</Typography>
              )}
            </FormControl>
          </Grid>
          {/* Row 9 */}
          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="allowOverAllocation">
                Allow over allocation
              </Typography>
              {!readOnly ? (
                <Select
                  id="allowOverAllocation"
                  name="allowOverAllocation"
                  value={stepData.allowOverAllocation}
                  onChange={handleChange}
                  variant="outlined"
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select from the list";
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="YES">YES</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </Select>
              ) : (
                <Typography>{stepData.allowOverAllocation}</Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl className={classes.formField} fullWidth>
              <Typography component="label" htmlFor="overAllocationLimit" style={{color: stepData.allowOverAllocation === "NO" /* || !isOverAllocationAllowed() */ ? "#999" : "inherit" }}>
                Over allocation limit (%) (only if over allocation is YES)
              </Typography>
              {!readOnly ? (
                <TextField
                  id="overAllocationLimit"
                  name="overAllocationLimit"
                  value={stepData.allowOverAllocation === "NO" /* || !isOverAllocationAllowed() */ ? "NA" : stepData.overAllocationLimit}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  disabled={stepData.allowOverAllocation === "NO" /* || !isOverAllocationAllowed() */}
                  InputProps={{
                    style: commonInlineStyles.input,
                    onBeforeInput: (e) =>  {
                      const v = e.target.value;
                      const nv = v.slice(0, e.target.selectionStart) + e.data + v.slice(e.target.selectionEnd);
                      if (!/^\d+$/.test(e.data) || +nv < 1 || +nv > 100) e.preventDefault();
                    }
                  }}
                  inputProps={commonInlineStyles.inputProps}
                />
              ) : (
                <Typography>{stepData.allowOverAllocation === "NO" /* || !isOverAllocationAllowed() */ ? "NA" : stepData.overAllocationLimit}</Typography>
              )}
            </FormControl>
          </Grid>

          
        </Grid>
      </form>
        {/* Note */}
      {/* Over-allocation restriction removed - can now be done for all product-material type combinations */}
      {/* <Box mt={4}>
        <Typography style={{ color: "#666"}} variant="body2">
          Note -<br />
          Over-allocation can be done only in these product-material type combinations - FG-Bulk DP, BNU-Bulk DP, FG-Vial, FG-BNU.
        </Typography>
      </Box> */}
      
      {/* {typeof handleNext === "function" &&
        typeof handleBack === "function" &&
        typeof activeStep === "number" &&
        typeof stepsLength === "number" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: activeStep === 0 ? "center" : "space-between",
            }}
            className="stepper-navigation-btns"
            mt={4}
          >
            <Button
              variant="outlined"
              disabled={activeStep === 0}
              onClick={handleBack}
              className={classes.button}
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.preventDefault();
                if (activeStep === stepsLength - 1) {
                  handleSubmit();
                } else {
                  handleNext();
                }
              }}
              className={classes.button}
            >
              Save & Next
            </Button>
          </Box>
        )}
    </div>
  );
}; */}

     {/* Navigation Buttons */}
      {(!readOnly && !viewOnlyProductDetails && !isAllocationConfigEditMode) &&
        <Box
          sx={{
            display: "flex",
            justifyContent: activeStep === 0 ? "center" : "space-between",
          }}
          className="stepper-navigation-btns"
          mt={4}
        >
          {activeStep !== 0 && (
            <>            <Button
              variant="outlined"
              disabled={activeStep === 0}
              onClick={handleBack}
              className={classes.button}
            >
              Back
            </Button>
              <Box sx={{ flex: "1 1 auto" }} />
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={activeStep === stepsLength - 1 ? handleSubmit : saveAllocationsConfigurations}
            className={classes.button}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : activeStep === stepsLength - 1 ? (
              "Save"
            ) : (
              "Save & Next"
            )}
          </Button>
        </Box>
      }

      {/* Error Snackbar */}
      <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        style={{ zIndex: 9999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          style={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default React.memo(AllocationConfigurations);